/**
 * `<PrefetchAnchor>` — a plain `<a href>` (full document reload on click)
 * that prefetches the target URL on hover/focus to warm the edge + browser
 * HTTP cache.
 *
 * Why not `<link rel="prefetch">`: Chromium schedules `<link rel="prefetch">`
 * at the lowest priority class (Idle). On a busy page (PLP with shelves,
 * _serverFn calls, images), the idle scheduler never fires during the
 * ~200–500 ms window between hover and click — so the prefetch sits queued
 * and the click pays full network cost anyway. Verified empirically in
 * DevTools: hover on Header link → zero prefetch request appears.
 *
 * Why not `router.preloadRoute()`: short-circuits on `cachedMatches`
 * (router.js:1006), and only runs the route loader (server fn) — never
 * fetches the HTML document — so the browser HTTP cache stays cold.
 *
 * What we do instead: fire a plain `fetch(href)` on hover/focus after a
 * short intent delay.
 *   1. Hover starts a HOVER_DELAY_MS timer.
 *   2. If the cursor leaves before the timer fires, we cancel — avoids
 *      spamming fetches when the user is just sweeping the mouse across
 *      a long category list to reach a different target.
 *   3. If the cursor stays, we fire `fetch(href)`. Same-origin GET goes
 *      through the Cloudflare edge cache (warming the worker's cached
 *      HTML) and lands in the browser HTTP cache with the same Vary
 *      semantics as the eventual navigation.
 *   4. On click, the browser HTTP cache serves the response — instant TTFB.
 *
 * Module-level `Set<string>` dedupes per-URL across the document so the
 * second hover on the same href is a no-op.
 */
import * as React from "react";

/**
 * How long the cursor must rest on a link before we fire the prefetch.
 * Shorter = more eager but more wasted fetches on mouse sweep.
 * Longer = stingier but click may beat the prefetch.
 * 80 ms is short enough that intentional hover always commits before a
 * typical 200+ ms hover→click, but filters out mouse-sweep transit.
 */
const HOVER_DELAY_MS = 80;

export interface PrefetchAnchorProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Required target URL, same shape as `<a href>` (relative path or absolute URL). */
  href: string;
}

const prefetched = new Set<string>();

function shouldPrefetch(href: string | undefined): href is string {
  if (!href) return false;
  if (href.startsWith("#")) return false;
  if (href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("javascript:")) return false;
  if (typeof window !== "undefined" && href.startsWith("http")) {
    try {
      const u = new URL(href);
      if (u.origin !== window.location.origin) return false;
    } catch {
      return false;
    }
  }
  return true;
}

export function prefetchUrl(href: string): void {
  if (typeof window === "undefined") return;
  if (prefetched.has(href)) return;
  prefetched.add(href);

  fetch(href, {
    method: "GET",
    credentials: "include",
    // Chrome 102+ priority hint; ignored elsewhere. Cast keeps it
    // type-safe whether or not the DOM lib in use declares `priority`.
    priority: "low",
  } as RequestInit & { priority: "low" }).catch(() => {
    prefetched.delete(href);
  });
}

export default function PrefetchAnchor({
  href,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  ...rest
}: PrefetchAnchorProps) {
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (shouldPrefetch(href) && !prefetched.has(href)) {
      clearPending();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        prefetchUrl(href);
      }, HOVER_DELAY_MS);
    }
    onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    clearPending();
    onMouseLeave?.(e);
  };

  // Focus (keyboard nav, screen reader) signals intent immediately — no delay.
  const handleFocus = (e: React.FocusEvent<HTMLAnchorElement>) => {
    if (shouldPrefetch(href)) prefetchUrl(href);
    onFocus?.(e);
  };

  React.useEffect(() => clearPending, []);

  return (
    <a
      href={href}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      {...rest}
    />
  );
}
